import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import { format } from 'date-fns';
import { useGetBroadcastQuery } from '../../../services/notificationService/__generated__/queries.generated';
import { broadcastContentToText } from '../../../utils/broadcastContent';

const Container = styled.div`
  ${tw`mx-auto w-full max-w-screen-xl min-w-0 box-border px-4 pt-6 pb-8 sm:p-6 sm:pb-8 md:pb-16`}
`;

const BackButton = styled.button`
  ${tw`flex items-center gap-2 text-sm font-medium text-text-secondary hover:text-text-primary mb-6`}
`;

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.5 15L7.5 10L12.5 5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Card = styled.div`
  ${tw`bg-white rounded-card shadow-card border border-border-default overflow-hidden`}
`;

const CardHeader = styled.div`
  ${tw`bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-8 text-white`}
`;

const Title = styled.h1`
  ${tw`text-2xl md:text-3xl font-bold mb-2`}
`;

const Meta = styled.div`
  ${tw`text-text-tertiary text-sm`}
`;

const CardContent = styled.div`
  ${tw`p-6 md:p-8`}
`;

const Summary = styled.p`
  ${tw`text-lg text-text-secondary mb-6 pb-6 border-b border-border-default`}
`;

const Content = styled.div`
  ${tw`prose prose-sm max-w-none text-text-secondary leading-relaxed`}
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    ${tw`text-text-primary font-semibold`}
  }

  p {
    ${tw`text-text-secondary leading-relaxed`}
  }

  ul,
  ol {
    ${tw`text-text-secondary`}
  }

  a {
    ${tw`text-text-secondary hover:text-text-primary`}
  }
`;

const LoadingWrapper = styled.div`
  ${tw`flex justify-center items-center py-16`}
`;

const ErrorState = styled.div`
  ${tw`py-16 text-center`}
`;

const ErrorTitle = styled.div`
  ${tw`text-lg font-semibold text-text-secondary mb-2`}
`;

const ErrorSubtitle = styled.div`
  ${tw`text-sm text-text-secondary`}
`;

const BroadcastDetailPage: React.FC = () => {
  const { broadcastId } = useParams<{ broadcastId: string }>();
  const navigate = useNavigate();

  const { data, loading, error } = useGetBroadcastQuery({
    variables: { id: broadcastId || '' },
    skip: !broadcastId,
  });

  const broadcast = data?.broadcast;

  const handleBack = () => {
    navigate('/chat?tab=notifications');
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return format(new Date(dateString), "MMMM d, yyyy 'at' h:mm a");
  };

  if (loading) {
    return (
      <Container>
        <BackButton onClick={handleBack}>
          <BackIcon />
          Back to Notifications
        </BackButton>
        <LoadingWrapper>
          <div>Loading announcement...</div>
        </LoadingWrapper>
      </Container>
    );
  }

  if (error || !broadcast) {
    return (
      <Container>
        <BackButton onClick={handleBack}>
          <BackIcon />
          Back to Notifications
        </BackButton>
        <ErrorState>
          <ErrorTitle>Announcement not found</ErrorTitle>
          <ErrorSubtitle>
            {error?.message || 'This announcement may have been removed or is no longer available.'}
          </ErrorSubtitle>
        </ErrorState>
      </Container>
    );
  }

  return (
    <Container>
      <BackButton onClick={handleBack}>
        <BackIcon />
        Back to Notifications
      </BackButton>

      <Card>
        <CardHeader>
          <Title>{broadcast.title}</Title>
          <Meta>Posted {formatDate(broadcast.sentAt || broadcast.createdAt)}</Meta>
        </CardHeader>
        <CardContent>
          {broadcast.summary && <Summary>{broadcast.summary}</Summary>}
          {/* Plain text, as in the app: a brand admin's message must not run in the page. */}
          <Content>{broadcastContentToText(broadcast.content)}</Content>
        </CardContent>
      </Card>
    </Container>
  );
};

export default BroadcastDetailPage;
